import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, Trash2, Upload, FileText } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtDateIST, inr } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";

const fmtSize = (bytes) => {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function BillingClientPage({ embeddedClientId = null }) {
  const params = useParams();
  const clientId = embeddedClientId || params.clientId;
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/billing/client/${clientId}`);
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      await api.post(`/billing/client/${clientId}/bills`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Bill "${file.name}" uploaded.`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (bill) => {
    try {
      const res = await api.get(`/billing/bills/${bill.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = bill.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/billing/bills/${deleteTarget.id}`);
      toast.success("Bill deleted.");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleBilled = async (order) => {
    setToggling(order.id);
    try {
      await api.post(`/orders/${order.id}/billed`, { billed: !order.billed });
      toast.success(`Order ${order.order_id} marked ${order.billed ? "unbilled" : "billed"}.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setToggling(null);
    }
  };

  const toggleSelect = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const toggleSelectAll = () =>
    setSelected((s) => (data && s.size === data.orders.length ? new Set() : new Set((data?.orders || []).map((o) => o.id))));

  const bulkUpdate = async (field, value) => {
    setBulkLoading(true);
    try {
      const { data: res } = await api.post("/orders/bulk-billing", { order_ids: [...selected], field, value });
      toast.success(`${res.updated} order(s) updated.`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleBillPaid = async (bill) => {
    try {
      await api.post(`/billing/bills/${bill.id}/paid`, { paid: !bill.paid });
      toast.success(`Invoice marked ${bill.paid ? "unpaid" : "paid"}.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (loading) {
    return <div className="flex justify-center py-24" data-testid="billing-client-loading"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!data) return null;

  const { client, orders, bills } = data;
  const billedAmount = orders.filter((o) => o.billed).reduce((s, o) => s + (o.total_amount || 0), 0);
  const unbilledAmount = orders.filter((o) => !o.billed).reduce((s, o) => s + (o.total_amount || 0), 0);
  const paidAmount = orders.filter((o) => o.paid).reduce((s, o) => s + (o.total_amount || 0), 0);

  return (
    <div className="space-y-5" data-testid="billing-client-page">
      <div className="flex items-center gap-3 flex-wrap">
        {!embeddedClientId && (
          <button onClick={() => navigate("/billing")} data-testid="billing-back-button" className="p-2 rounded-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors duration-150">
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900" data-testid="billing-client-title">{isClient ? "My Billing" : client.company_name}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{isClient ? `${client.company_name} · bills and payment status for your orders` : `${client.email} · ${client.contact_person}`}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="billing-client-stats">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-green-50 border border-green-200 text-green-700 rounded-full px-3 py-1.5 tnum" data-testid="client-billed-amount">Billed: {inr(billedAmount)}</span>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-orange-50 border border-orange-200 text-orange-700 rounded-full px-3 py-1.5 tnum" data-testid="client-unbilled-amount">Unbilled: {inr(unbilledAmount)}</span>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-3 py-1.5 tnum" data-testid="client-paid-amount">Paid: {inr(paidAmount)}</span>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-50 border border-slate-200 text-slate-600 rounded-full px-3 py-1.5 tnum">{orders.filter((o) => o.billed).length}/{orders.length} orders billed</span>
      </div>

      {/* Upload */}
      {!isClient && (
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-[0_1px_2px_rgba(15,23,42,0.05)]" data-testid="upload-bill-card">
        <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">Upload Bill</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={upload}
            disabled={uploading}
            data-testid="bill-file-input"
            className="text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-[#152D53] file:cursor-pointer"
          />
          {uploading && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
        </div>
        <p className="text-xs text-slate-400 mt-2">PDF, JPG, PNG or WEBP · max 10 MB</p>
      </div>
      )}

      {/* Bills list */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.05)]" data-testid="bills-list-card">
        <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 px-5 py-4 border-b border-slate-100">{isClient ? "Invoices / Bills" : "Uploaded Bills"} ({bills.length})</h3>
        {bills.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid="bills-empty">No bills uploaded yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {bills.map((b) => (
              <div key={b.id} className="flex items-center gap-3 px-4 md:px-5 py-3" data-testid={`bill-row-${b.id}`}>
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{b.original_filename}</div>
                  <div className="text-xs text-slate-500">{fmtSize(b.size)} · {b.uploaded_by} · {fmtDateIST(b.uploaded_at)}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-sm text-xs font-medium border whitespace-nowrap ${b.paid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid={`bill-paid-badge-${b.id}`}>
                  {b.paid ? "Paid" : "Unpaid"}
                </span>
                {!isClient && (
                  <button
                    onClick={() => toggleBillPaid(b)}
                    data-testid={`bill-paid-toggle-${b.id}`}
                    className="text-xs font-semibold text-primary hover:underline whitespace-nowrap"
                  >
                    {b.paid ? "Mark Unpaid" : "Mark Paid"}
                  </button>
                )}
                <button onClick={() => download(b)} data-testid={`bill-download-${b.id}`} title="Download" className="p-1.5 rounded-sm text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors duration-150">
                  <Download className="h-4 w-4" />
                </button>
                {!isClient && (
                  <button onClick={() => setDeleteTarget(b)} data-testid={`bill-delete-${b.id}`} title="Delete" className="p-1.5 rounded-sm text-slate-500 hover:text-[#E63946] hover:bg-[#E63946]/10 transition-colors duration-150">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Orders billing status */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.05)]" data-testid="billing-orders-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-wrap gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Orders ({orders.length})</h3>
          {orders.length > 0 && !isClient && (
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={selected.size === orders.length}
                onChange={toggleSelectAll}
                data-testid="select-all-orders"
                className="h-4 w-4 accent-[#1B3A6B]"
              />
              Select all
            </label>
          )}
        </div>
        {selected.size > 0 && !isClient && (
          <div className="flex items-center gap-2 px-4 md:px-5 py-3 bg-slate-50 border-b border-slate-100 flex-wrap" data-testid="bulk-actions-bar">
            <span className="text-xs font-semibold text-slate-700 tnum mr-1">{selected.size} selected</span>
            <Button size="sm" disabled={bulkLoading} onClick={() => bulkUpdate("billed", true)} data-testid="bulk-mark-billed" className="bg-green-600 hover:bg-green-700 rounded-sm h-8 text-xs">Mark Billed</Button>
            <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulkUpdate("billed", false)} data-testid="bulk-mark-unbilled" className="rounded-sm h-8 text-xs border-slate-300">Mark Unbilled</Button>
            <Button size="sm" disabled={bulkLoading} onClick={() => bulkUpdate("paid", true)} data-testid="bulk-mark-paid" className="bg-emerald-600 hover:bg-emerald-700 rounded-sm h-8 text-xs">Mark Paid</Button>
            <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulkUpdate("paid", false)} data-testid="bulk-mark-unpaid" className="rounded-sm h-8 text-xs border-slate-300">Mark Unpaid</Button>
          </div>
        )}
        {orders.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">No orders for this client yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {orders.map((o) => (
              <div key={o.id} className="flex items-center gap-3 px-4 md:px-5 py-3 flex-wrap" data-testid={`billing-order-${o.order_id}`}>
                {!isClient && (
                  <input
                    type="checkbox"
                    checked={selected.has(o.id)}
                    onChange={() => toggleSelect(o.id)}
                    data-testid={`select-order-${o.order_id}`}
                    className="h-4 w-4 accent-[#1B3A6B] shrink-0"
                  />
                )}
                <button onClick={() => navigate(`/orders/${o.id}`)} className="font-semibold text-primary text-sm whitespace-nowrap">{o.order_id}</button>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 truncate">{o.passenger_name}</div>
                  <div className="text-xs text-slate-500">{fmtDateIST(o.created_at)} · {o.total_amount != null ? inr(o.total_amount) : "—"}</div>
                </div>
                <StatusBadge status={o.delivery_status} />
                <span className={`px-2 py-0.5 rounded-sm text-xs font-medium border ${o.billed ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-500 border-slate-200"}`} data-testid={`billed-badge-${o.order_id}`}>
                  {o.billed ? "Billed" : "Unbilled"}
                </span>
                <span className={`px-2 py-0.5 rounded-sm text-xs font-medium border ${o.paid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid={`paid-badge-${o.order_id}`}>
                  {o.paid ? "Paid" : "Unpaid"}
                </span>
                {!isClient && (
                  <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={toggling === o.id}
                  onClick={() => toggleBilled(o)}
                  data-testid={`toggle-billed-${o.order_id}`}
                  className={`rounded-sm ${o.billed ? "border-slate-300" : "border-green-600 text-green-700 hover:bg-green-50"}`}
                >
                  {toggling === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : o.billed ? "Mark Unbilled" : "Mark Billed"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={toggling === o.id}
                  onClick={async () => {
                    setToggling(o.id);
                    try {
                      await api.post("/orders/bulk-billing", { order_ids: [o.id], field: "paid", value: !o.paid });
                      toast.success(`Order ${o.order_id} marked ${o.paid ? "unpaid" : "paid"}.`);
                      load();
                    } catch (e) { toast.error(apiError(e)); } finally { setToggling(null); }
                  }}
                  data-testid={`toggle-paid-${o.order_id}`}
                  className={`rounded-sm ${o.paid ? "border-slate-300" : "border-emerald-600 text-emerald-700 hover:bg-emerald-50"}`}
                >
                  {o.paid ? "Mark Unpaid" : "Mark Paid"}
                </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this bill?"
        description={`"${deleteTarget?.original_filename}" will be removed from the billing records.`}
        confirmLabel="Delete Bill"
        destructive
        onConfirm={confirmDelete}
        testId="delete-bill-dialog"
      />
    </div>
  );
}
